<template>
    <div class="container">

        <!-- Back Button -->
        <button @click="goBack" class="btn btn-back mt-1 mb-3">
            <i class="fas fa-arrow-left"></i>
        </button>

        <!-- Edit Mode Restriction Message -->
        <div v-if="!canEdit && !isEditMode && !isLoading" class="edit-restriction-message">
            <div class="restriction-icon">
                <svg style="height: 20px; width: 20px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            </div>
            <span>{{ getEditRestrictionMessage() }}</span>
        </div>

        <!-- Page Header -->
        <div class="page-header py-2">
            <div class="flex items-center gap-4">
                <h1 class="text-[#333] text-[30px] font-semibold m-0">
                    {{ isEditMode ? 'Editar Reserva' : 'Detalhes da Reserva' }}
                    <span v-if="reservation" class="reservation-id">
                        #{{ reservation.reservationId }}
                    </span>
                </h1>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-container">
            <div class="loading-card">
                <div class="loading-content">
                    <div class="loading-spinner"></div>
                    <div>
                        <h3 class="loading-title">{{ isEditMode ? 'A carregar dados para edição...' :
                            'A carregar detalhes...' }}</h3>
                        <p class="loading-text">Por favor aguarde...</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="!reservation && !isLoading" class="error-container">
            <div class="error-header">
                <div class="error-icon">
                    <svg style="height: 24px; width: 24px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 class="error-title">Reserva não encontrada</h3>
            </div>
            <p class="error-message">Não foi possível encontrar a reserva solicitada.</p>
            <div class="error-actions">
                <button @click="loadReservation" class="btn">
                    Tentar novamente
                </button>
            </div>
        </div>

        <!-- Reservation Details/Edit Form -->
        <div v-else-if="reservation" class="form-container">
            <!-- Status Header -->
            <div class="status-header">
                <div class="status-row">
                    <div class="status-left">
                        <span class="status-label">Estado atual:</span>
                        <span class="status-badge" :class="getStatusClass(formData.status)">
                            {{ $t(`common.scheduledReservationStatus.${formData.status}`) }}
                        </span>
                    </div>
                    <div class="status-created">
                        Criado em: {{ formatDate(reservation.creationDate) }}
                    </div>
                </div>
            </div>


            <!-- Form Fields -->
            <form @submit.prevent="saveReservation" class="p-5">
                <div class="form-grid">

                    <div class="flex flex-col gap-3">
                        <h3 class="text-base font-semibold text-gray-900 border-b border-gray-200 pb-[6px] m-0">
                            Dados do pedido
                        </h3>

                        <div v-if="isCanceledReservation && reservation.cancelReason !== null" class="form-row-full">
                            <div class="form-field">
                                <label class="form-label">Motivo do cancelamento</label>
                                <input type="text"
                                    :value="$t(`common.ReservationCancelReason.${reservation.cancelReason}`)" readonly
                                    class="form-input-readonly">
                            </div>
                        </div>



                        <div class="form-row-full">
                            <div class="form-field">
                                <label class="form-label">ID da Reserva</label>
                                <input type="text" :value="reservation.reservationId" readonly
                                    class="form-input-readonly">
                            </div>
                        </div>

                        <div class="form-row-full">
                            <div class="form-field">
                                <label class="form-label">Funcionário</label>
                                <input type="text" :value="reservation.employee" readonly class="form-input-readonly">
                            </div>
                        </div>

                        <div class="form-row-full">
                            <div class="form-field">
                                <label class="form-label">Notas</label>

                                <div v-html="reservation.note"
                                    class="form-input-readonly rounded p-3 h-72 overflow-y-auto bg-gray-50" </div>

                                </div>
                            </div>

                            <div class=" form-row">
                                <div class="form-field">
                                    <label class="form-label">Número de Lugares</label>
                                    <input type="number" :value="reservation.numberOfSeats" readonly
                                        class="form-input-readonly">
                                </div>

                                <div class="form-field">
                                    <label class="form-label">Destino</label>
                                    <input type="text" :value="reservation.destination" readonly
                                        class="form-input-readonly">
                                </div>



                            </div>

                            <div class=" form-row">
                                <div class="form-field">
                                    <label class="form-label">Precisa de Motorista</label>
                                    <input type="string" :value="$t(`common.boolean.${reservation.needDriver}`)"
                                        readonly class="form-input-readonly" />
                                </div>

                            </div>

                            <!-- Start Date and End Date - Same row -->
                            <div class="form-row">
                                <div class="form-field">
                                    <label class="form-label">Data de Início</label>
                                    <Datepicker v-if="isEditMode" v-model="formData.startDate" :format-locale="pt"
                                        :format="formatDateTime" locale="pt-PT" :clearable="false"
                                        placeholder="Selecionar data" :enable-time-picker="true"
                                        select-text="Selecionar" cancel-text="Cancelar" now-button-label="Agora"
                                        class="form-datepicker" @update:model-value="onDateChange" />
                                    <input v-else type="text" :value="formatDate(formData.startDate)" readonly
                                        class="form-input-readonly">
                                </div>
                                <div class="form-field">
                                    <label class="form-label">Data de Fim</label>
                                    <Datepicker v-if="isEditMode" v-model="formData.endDate" :format-locale="pt"
                                        :format="formatDateTime" locale="pt-PT" :clearable="false"
                                        placeholder="Selecionar data" :enable-time-picker="true"
                                        select-text="Selecionar" cancel-text="Cancelar" now-button-label="Agora"
                                        :min-date="new Date(formData.startDate)" class="form-datepicker"
                                        @update:model-value="onDateChange" />
                                    <input v-else type="text" :value="formatDate(formData.endDate)" readonly
                                        class="form-input-readonly">
                                </div>
                            </div>



                            <h3 class="text-base font-semibold text-gray-900 border-b border-gray-200 pb-[6px] m-0">
                                Atribuição de veículo
                            </h3>


                            <div class="form-row">
                                <!-- Vehicle Selection (only in edit mode) -->
                                <div v-if="isEditMode" class="form-row-full">
                                    <div class="form-field">
                                        <label class="form-label">
                                            Selecionar Veículo Disponível
                                            <span v-if="vehicleStore.isLoading" class="loading-indicator">(A
                                                carregar...)</span>
                                        </label>
                                        <div ref="dropdown" class="relative">
                                            <!-- Input Field with Clear Button -->
                                            <input ref="input" type="text" v-model="searchTerm" @input="filterOptions"
                                                @focus="openDropdown" placeholder="Selecione um veículo"
                                                class="w-full px-4 py-2 border rounded-lg bg-white focus:ring-blue-500 form-select"
                                                :disabled="vehicleStore.isLoading" />
                                            <!-- Dropdown options that appear below the input -->
                                            <div v-if="filteredOptions.length > 0 && isDropdownOpen"
                                                class="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg max-h-60 overflow-y-auto z-10">
                                                <ul>
                                                    <li v-for="(option) in filteredOptions" :key="option.value"
                                                        @click="selectOption(option)"
                                                        class="px-4 py-2 cursor-pointer hover:bg-gray-200">
                                                        {{ option.label }}
                                                    </li>
                                                </ul>
                                            </div>

                                            <!-- Hidden Select to bind value -->
                                            <select v-model="selectedVehicleId" @change="onVehicleSelect"
                                                class="hidden">
                                                <option value="">Selecione um veículo</option>
                                                <option v-for="option in filteredOptions" :key="option.value"
                                                    :value="option.value">
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                            <button v-if="selectedVehicleId" @click="clearSelection"
                                                class="absolute right-5 self-center text-gray-500 hover:text-gray-700 focus:outline-none">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                    stroke="currentColor" class="w-5 h-5">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div v-if="vehicleStore.error" class="error-text">
                                            {{ vehicleStore.error }}
                                        </div>
                                        <div v-if="!vehicleStore.isLoading && !vehicleStore.hasAvailableVehicles && hasValidDates"
                                            class="warning-text">
                                            Nenhum veículo disponível para as datas selecionadas.
                                        </div>
                                        <div v-if="!hasValidDates" class="info-text">
                                            Selecione as datas de início e fim para ver veículos disponíveis.
                                        </div>
                                    </div>
                                </div>


                                <!-- PickupLocation Selection (only in edit mode) -->
                                <div v-if="isEditMode" class="form-row-full">
                                    <div class="form-field">
                                        <label class="form-label">
                                            Selecionar Local de recolha
                                            <span v-if="locationStore.isLoading" class="loading-indicator">(A
                                                carregar...)</span>
                                        </label>
                                        <div ref="dropdownLocation" class="relative">
                                            <!-- Input Field with Clear Button -->
                                            <input ref="inputLocation" type="text" v-model="searchTermLocation"
                                                @input="locationFilterOptions" @focus="locationOpenDropdown"
                                                placeholder="Selecione um local de recolha"
                                                class="w-full px-4 py-2 border rounded-lg bg-white focus:ring-blue-500 form-select"
                                                :disabled="locationStore.isLoading" />
                                            <!-- Dropdown options that appear below the input -->
                                            <div v-if="locationFilteredOptions.length > 0 && isLocationDropdownOpen"
                                                class="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg max-h-60 overflow-y-auto z-10">
                                                <ul>
                                                    <li v-for="location in locationFilteredOptions" :key="location.name"
                                                        @click="selectLocation(location)"
                                                        class="px-4 py-2 cursor-pointer hover:bg-gray-200">
                                                        {{ location.name }}
                                                    </li>
                                                </ul>
                                            </div>

                                            <!-- Hidden Select to bind value -->
                                            <select v-model="selectedLocationId" @change="onLocationSelect"
                                                class="hidden">
                                                <option value="">Selecione o local de recolha</option>
                                                <option v-for="location in locationFilteredOptions" :key="location.name"
                                                    :value="location.name">
                                                    {{ location.name }}
                                                </option>
                                            </select>

                                            <button v-if="selectedLocationId" @click="clearLocationSelection"
                                                class="absolute right-5 self-center text-gray-500 hover:text-gray-700 focus:outline-none">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                    stroke="currentColor" class="w-5 h-5">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div v-if="locationStore.error" class="error-text">
                                            {{ locationStore.error }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="!isEditMode" class="form-row">
                                <div class="form-field">
                                    <label class="form-label">Local de recolha do veículo</label>
                                    <input type="text" v-model="formData.pickupLocation" readonly
                                        class="form-input-readonly" />
                                </div>
                            </div>

                            <!-- Vehicle Details -->
                            <div class="form-row">
                                <div class="form-field">
                                    <label class="form-label">Marca do Veículo</label>
                                    <input type="text" v-model="formData.vehicle.brand" readonly
                                        class="form-input-readonly" :placeholder="isEditMode ? 'Ex: Toyota' : ''">
                                </div>

                                <div class="form-field">
                                    <label class="form-label">Modelo</label>
                                    <input type="text" v-model="formData.vehicle.model"
                                        :placeholder="isEditMode ? 'Ex: Corolla' : ''" readonly
                                        class="form-input-readonly">
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field">
                                    <label class="form-label">Matrícula</label>
                                    <input type="text" v-model="formData.vehicle.licensePlate"
                                        :placeholder="isEditMode ? 'Ex: 12-AB-34' : ''" readonly
                                        class="form-input-readonly">
                                </div>

                                <div class="form-field">
                                    <label class="form-label">Número de Lugares</label>
                                    <input type="text"
                                        :value="formData.vehicle.numberOfSeats === 0 ? '' : formData.vehicle.numberOfSeats"
                                        readonly class="form-input-readonly" :placeholder="isEditMode ? 'Ex: 5' : ''" />
                                </div>
                            </div>

                        </div>
                    </div>

                    <!-- Action Buttons at Bottom -->
                    <div class="form-footer">
                        <!-- View Mode Buttons -->
                        <template v-if="!isEditMode">
                            <button type="button" @click="goBack" class="btn btn-back">
                                Fechar
                            </button>
                            <button type="button" @click="toggleEditMode" class="btn btn-primary" v-if="canEdit">
                                Editar Reserva
                            </button>
                        </template>

                        <!-- Edit Mode Buttons -->
                        <template v-else>
                            <button type="button" @click="cancelEdit" class="btn btn-cancel">
                                Cancelar
                            </button>
                            <button type="button" @click="resetForm" class="btn btn-secondary">
                                Repor
                            </button>
                            <button type="submit" class="btn btn-save" :disabled="isSaving || !hasChanges">
                                <span v-if="isSaving">A guardar...</span>
                                <span v-else>Guardar Alterações</span>
                            </button>
                        </template>
                    </div>
            </form>
        </div>
    </div>
</template>

<script lang="ts">
// Import required components and utilities
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { useReservationStore } from '@/modules/reservations/store/reservation-store'
import { useVehicleStore } from '@/modules/reservations/store/vehicle-store'
import { useLocationStore } from '@/modules/reservations/store/location-store'
import { useToast } from '@/core/composables/use-toast'
import { pt } from 'date-fns/locale'
import { ReservationStatus } from '@/modules/reservations/models/reservation-status.enum'
import { VehicleOption } from '../models/vehicle'
import { ReservationCancelReasonForm } from '../models/reservationCancelReason.enum'


export default {
    name: 'ReservationDetailsEditPage',

    components: {
        Datepicker
    },

    setup() {
        const { showError, showSuccess, showWarning, showInfo } = useToast()

        return {
            showError,
            showSuccess,
            showWarning,
            showInfo
        }
    },

    data() {
        const formData = {
            employee: '',
            vehicleType: '',
            destination: '',
            numberOfSeats: 1,
            startDate: 0,
            endDate: 0,
            status: ReservationStatus.Open,
            note: '',
            vehicle: {
                id: '',
                brand: '',
                model: '',
                licensePlate: '',
                numberOfSeats: 0
            },
            needDriver: false,
            cancelReason: null as ReservationCancelReasonForm,
            pickupLocation: ''
        };
        type FormDataType = typeof formData;
        return {
            store: useReservationStore(),
            vehicleStore: useVehicleStore(),
            locationStore: useLocationStore(),

            reservationId: '',
            isEditMode: false,
            isSaving: false,
            selectedVehicleId: '',
            selectedLocationId: '',

            searchTerm: '',
            searchTermLocation: '',
            filteredOptions: [] as VehicleOption[],
            locationFilteredOptions: [] as { name: string; id: string }[],
            isDropdownOpen: false,
            isLocationDropdownOpen: false,

            // Form data for editing
            formData,

            // Original data for comparison and reset
            originalData: null as FormDataType | null,

            // Status options
            statusOptions: [
                { label: 'Aberto', value: 'Open' },
                { label: 'Confirmado', value: 'Confirmed' },
                { label: 'Fechado', value: 'Closed' },
                { label: 'Cancelado', value: 'Cancelled' }
            ],

            // Date picker configuration
            pt,
            formatDateTime: 'dd/MM/yyyy HH:mm'
        }
    },

    computed: {
        // Get vehicle options from store (ensure it is available in your state)
        vehicleOptions() {
            return this.vehicleStore.vehicleOptions
        },
        locations() {
            return this.locationStore.locations
        },
        reservation() {
            return this.store.selectedReservation
        },
        isLoading() {
            return this.store?.isLoading !== undefined ? this.store.isLoading : true
        },

        // Check if reservation can be edited
        canEdit() {
            if (!this.reservation) return false

            switch (this.reservation?.status) {
                case ReservationStatus.Closed:
                case ReservationStatus.Canceled: {
                    return false;
                }
                case ReservationStatus.Confirmed:
                case ReservationStatus.Open: {
                    return true;
                }
            }
        },
        isCanceledReservation() {
            if (!this.reservation) return false

            switch (this.reservation?.status) {
                case ReservationStatus.Canceled: {
                    return true;
                }
                case ReservationStatus.Closed:
                case ReservationStatus.Confirmed:
                case ReservationStatus.Open: {
                    return false;
                }
            }
        },
        // Check if dates are valid for vehicle search
        hasValidDates() {
            if (!this.formData.startDate || !this.formData.endDate) return false

            const startDate = new Date(this.formData.startDate)
            const endDate = new Date(this.formData.endDate)

            return startDate !== null && endDate > startDate
        },

        // Check if form has changes compared to original data
        hasChanges() {
            if (!this.originalData) return false
            const keys = Object.keys(this.formData) as (keyof typeof this.formData)[];

            return keys.some(key => {
                if (key === 'startDate' || key === 'endDate') {
                    // Compare dates properly
                    const current = this.formData[key]
                    const original = this.originalData![key]
                    return new Date(current).getTime() !== new Date(original).getTime()
                }
                if (key === 'vehicle') {
                    // Compare vehicle object properties
                    const vehicleKeys = Object.keys(this.formData.vehicle) as (keyof typeof this.formData.vehicle)[];
                    return vehicleKeys.some(vehicleKey =>
                        this.formData.vehicle[vehicleKey] !== this.originalData!.vehicle[vehicleKey]
                    )
                }
                if (key === 'pickupLocation') {
                    // Compare location object properties
                    //return Object.keys(this.formData.pickupLocation).some(locationKey => {
                    //console.log(this.formData.pickupLocation)
                    //console.log(this.originalData!.pickupLocation)
                    return this.formData.pickupLocation !== this.originalData!.pickupLocation

                    // })
                }
                return this.formData[key] !== this.originalData![key]
            })
        }
    },

    // Get reservation ID from route params and determine mode
    created() {
        this.reservationId = this.$route.params.id.toString()
        // Determine if we're in edit mode based on route
        this.isEditMode = this.$route.name === 'ReservationEdit' || this.$route.path.includes('/edit')

        if (this.reservationId) {
            this.loadReservation()
        } else {
            this.showError({message:'ID da reserva não fornecido', title:'Erro'})
        }
    },

    // Clear selected reservation when component is destroyed
    beforeUnmount() {
        if (this.store) {
            this.store.clearSelectedReservation()
        }
        if (this.vehicleStore) {
            this.vehicleStore.clearAvailableVehicles()
            this.vehicleStore.clearError()
        }
        if (this.locationStore) {
            this.locationStore.resetStore()
            this.locationStore.clearError()
        }
    },

    methods: {
        /**
         * Load reservation details using store
         */
        async loadReservation() {
            if (!this.reservationId) {
                this.showError({message:'ID da reserva não fornecido', title:'Erro de Parâmetros'})
                return
            }

            try {
                await this.store.fetchReservationById(this.reservationId)

                if (this.reservation) {
                    if (this.isEditMode) {
                        this.formData.startDate = this.reservation.startDate
                        this.formData.endDate = this.reservation.endDate
                        await this.loadVehiclesForDates()
                        await this.loadLocations()
                    }
                    this.populateFormData()
                    // Load available vehicles based on reservation dates
                } else {
                    this.showWarning({message:'Reserva não encontrada', title:'Sem Resultados'})
                }

            } catch (error: any) {
                console.error('Error loading reservation:', error)

                if (error.response?.status === 404) {
                    this.showError({message:'Reserva não encontrada', title:'Reserva Inexistente'})
                } else if (error.response?.status === 403) {
                    this.showError({message:'Não tem permissões para ver esta reserva', title:'Acesso Negado'})
                } else {
                    this.showError({message:
                        'Erro ao carregar detalhes da reserva',
                        title:'Erro ao Carregar Reserva'}
                    )
                }
            }
        },

        /**
         * Populate form data from loaded reservation
         */
        populateFormData() {
            if (!this.reservation) return

            this.formData = {
                employee: this.reservation.employee || '',
                vehicleType: this.reservation.vehicleType || '',
                destination: this.reservation.destination || '',
                numberOfSeats: this.reservation.numberOfSeats || 1,
                startDate: new Date(this.reservation.startDate).getTime(),
                endDate: new Date(this.reservation.endDate).getTime(),
                status: this.reservation.status || 'Open',
                note: this.reservation.note || '',
                vehicle: {
                    id: this.reservation.vehicle?.id || '',
                    brand: this.reservation.vehicle?.brand || '',
                    model: this.reservation.vehicle?.model || '',
                    licensePlate: this.reservation.vehicle?.licensePlate || '',
                    numberOfSeats: this.reservation.vehicle?.numberOfSeats || 0
                },
                needDriver: this.reservation.needDriver,
                cancelReason: this.reservation.cancelReason || null,
                pickupLocation: this.reservation.pickupLocation || ''
            }

            // Set selected vehicle ID for the dropdown
            this.selectedVehicleId = this.formData.vehicle.id || ''

            this.selectedLocationId = this.formData.pickupLocation || ''
            this.searchTerm = this.selectedVehicleId !== '' ? `${this.formData.vehicle?.brand} ${this.formData.vehicle?.model} ${this.formData.vehicle?.licensePlate}` : '' //this.vehicleStore.vehicleOptions.label //
            this.searchTermLocation = this.selectedLocationId !== '' ? this.formData.pickupLocation : ''
            this.filteredOptions = this.vehicleOptions
            this.locationFilteredOptions = this.locations
            // Store original data for comparison and reset
            this.originalData = JSON.parse(JSON.stringify(this.formData))
        },



        async loadLocations() {
            this.locationStore.clearError()
            try {
                await this.locationStore.fetchAllLocations()

                if (this.locationStore.error) {
                    this.showError({message:this.locationStore.error, title:'Erro ao Carregar Veículos'})
                }
            } catch (error) {
                console.error('Error loading vehicles:', error)
                this.showError({message:'Erro ao carregar veículos disponíveis', title:'Erro'})
            }
        },
        /**
         * Load vehicles based on current dates
         */
        async loadVehiclesForDates() {
            // Clear any previous errors
            this.vehicleStore.clearError()

            const filters = {
                startDate: new Date(this.formData.startDate).getTime(),
                endDate: new Date(this.formData.endDate).getTime()
            }

            try {
                await this.vehicleStore.fetchAvailableVehicles(filters)

                if (this.vehicleStore.error) {
                    this.showError({message:this.vehicleStore.error, title:'Erro ao Carregar Veículos'})
                }
            } catch (error) {
                console.error('Error loading vehicles:', error)
                this.showError({message:'Erro ao carregar veículos disponíveis', title:'Erro'})
            }
        },
        /**
         * Toggle between view and edit modes
         */
        async toggleEditMode() {
            this.isEditMode = !this.isEditMode

            if (this.isEditMode) {
                // Refresh form data when entering edit mode
                this.populateFormData()
                // Load available vehicles
                await this.loadVehiclesForDates()
                await this.loadLocations()
            } else {
                // Clear vehicle store when exiting edit mode
                this.vehicleStore.clearAvailableVehicles()
                this.vehicleStore.clearError()
            }
        },

        /**
         * Cancel edit mode and revert changes
         */
        cancelEdit() {
            if (this.hasChanges) {
                if (confirm('Tem alterações não guardadas. Deseja descartar as alterações?')) {
                    this.resetForm()
                    this.isEditMode = false
                    this.vehicleStore.clearAvailableVehicles()
                    this.vehicleStore.clearError()
                }
            } else {
                this.isEditMode = false
                this.vehicleStore.clearAvailableVehicles()
                this.vehicleStore.clearError()
            }
        },

        /**
         * Reset form to original data
         */
        resetForm() {
            if (this.originalData) {
                this.formData = JSON.parse(JSON.stringify(this.originalData))
                this.selectedVehicleId = this.formData.vehicle.id || ''
                this.selectedLocationId = this.formData.pickupLocation || ''
                this.searchTerm = this.selectedVehicleId !== '' ? `${this.formData.vehicle?.brand} ${this.formData.vehicle?.model} ${this.formData.vehicle?.licensePlate}` : '' //this.option.label
                this.searchTermLocation = this.selectedLocationId !== '' ? this.formData.pickupLocation : ''
                this.isDropdownOpen = false
                this.isLocationDropdownOpen = false;
            }
        },
        /**
         * Opens the available vehicle dropdown
         */
        openDropdown() {
            this.isDropdownOpen = true;
            this.filterOptions();
        },

        locationOpenDropdown() {
            this.isLocationDropdownOpen = true;
            this.locationFilterOptions();
        },
        /**
         * Reset form to original data
         */
        clearSelection() {
            this.searchTerm = '';
            this.selectedVehicleId = '';
            this.filteredOptions = [];
            this.isDropdownOpen = false;
            this.onVehicleSelect();
            this.clearLocationSelection();
        },
        /**
         * Reset form to original data
         */
        clearLocationSelection() {
            this.searchTermLocation = '';
            this.selectedLocationId = '';
            this.locationFilteredOptions = [];
            this.isLocationDropdownOpen = false;
            this.onLocationSelect()
        },


        // Filter vehicle options based on search term
        filterOptions() {
            if (!this.searchTerm) {
                this.filteredOptions = [...this.vehicleStore.vehicleOptions]
                return;
            }

            const term = this.searchTerm?.toLowerCase();
            this.filteredOptions = this.vehicleOptions.filter(option =>
                option.label.toLowerCase().includes(term)
            );
            if (this.searchTerm.trim() === '' || this.searchTerm === `${this.formData.vehicle?.brand} ${this.formData.vehicle?.model} ${this.formData.vehicle?.licensePlate}`) {
                this.filteredOptions = [...this.vehicleStore.vehicleOptions]; // Show all options if no search term
            }
        },

        // Filter vehicle options based on search term
        locationFilterOptions() {
            if (!this.searchTermLocation) {
                this.locationFilteredOptions = [...this.locationStore.locations]
                return;
            }

            const term = this.searchTermLocation?.toLowerCase();
            this.locationFilteredOptions = this.locations.filter(option => option.name.toLowerCase().includes(term));
            if (this.searchTermLocation.trim() === '' || this.searchTermLocation === this.formData.pickupLocation) {
                this.locationFilteredOptions = [...this.locationStore.locations]; // Show all options if no search term
            }
        },

        /**
         * Changes the selectedOption when the available vehicle dropdown is opened
         */
        selectOption(option: { value: string; label: string }) {
            this.selectedVehicleId = option.value;
            this.searchTerm = option.label;  // Optionally display the selected label in the input
            this.filteredOptions = [];  // Close the dropdown after selection
            this.isDropdownOpen = false;
            this.onVehicleSelect();
        },

        selectLocation(location: { name: string }) {
            this.selectedLocationId = location.name;
            this.searchTermLocation = location.name;
            this.locationFilteredOptions = [];
            this.isLocationDropdownOpen = false;
            this.onLocationSelect();
        },



        /**
         * Handle date changes - load available vehicles when dates change
         */
        async onDateChange() {
            if (this.isEditMode) {
                // Add a small delay to ensure both dates are updated
                if (this.formData.startDate && this.formData.endDate) {
                    if (this.formData.startDate >= this.formData.endDate) {
                        this.showError({message:'A data de fim deve ser posterior à data de início'})
                        return
                    }
                }
                setTimeout(async () => {
                    await this.loadVehiclesForDates()
                }, 100)
            }
        },

        /**
         * Handle vehicle selection from dropdown
         */
        onVehicleSelect() {
            if (this.selectedVehicleId) {
                const selectedVehicle = this.vehicleStore.getAvailableVehicleById(this.selectedVehicleId)
                if (selectedVehicle) {
                    // Update form data with selected vehicle
                    this.formData.vehicle = {
                        id: selectedVehicle.id,
                        brand: selectedVehicle.brand || '',
                        model: selectedVehicle.model || '',
                        licensePlate: selectedVehicle.licensePlate || '',
                        numberOfSeats: selectedVehicle.numberOfSeats !== null ? selectedVehicle.numberOfSeats! : 0
                    }
                    //this.searchTerm = this.vehicleOptions.label
                }
            } else {
                // Clear vehicle data if no vehicle selected
                this.formData.vehicle = {
                    id: '',
                    brand: '',
                    model: '',
                    licensePlate: '',
                    numberOfSeats: 0
                }
                //this.searchTerm = '';
            }
        },

        /**
         * Handle location selection from dropdown
         */
        onLocationSelect() {
            if (this.selectedLocationId) {
                this.formData.pickupLocation = this.selectedLocationId
            } else {
                // Clear vehicle data if no vehicle selected
                this.formData.pickupLocation = ''
            }
        },




        /**
         * Get edit restriction message
         */
        getEditRestrictionMessage() {
            if (!this.reservation) return ''

            const status = this.reservation.status

            if ([ReservationStatus.Closed].includes(status)) {
                return 'Esta reserva não pode ser editada porque já decorreu.'
            }
            if ([ReservationStatus.Canceled].includes(status)) {
                return 'Esta reserva não pode ser editada porque foi cancelada.'
            }

            return ''
        },

        /**
        * Save reservation changes using the correct API format
        */
        async saveReservation() {
            if (!this.hasChanges) {
                this.showInfo({message:'Não há alterações para guardar'})
                return
            }

            // Validate required fields
            if (!this.formData.startDate || !this.formData.endDate) {
                this.showError({message:'Por favor preencha todos os campos obrigatórios'})
                return
            }

            // Validate date range
            if (this.formData.startDate >= this.formData.endDate) {
                this.showError({message:'A data de fim deve ser posterior à data de início'})
                return
            }

            // Validate that a vehicle is selected
            if (!this.formData.vehicle.id && this.selectedVehicleId) {
                this.showError({message:'Por favor selecione um veículo'})
                return
            }

            // Validate that a vehicle is selected
            if ((this.formData.vehicle.id && this.selectedVehicleId) && !this.formData.pickupLocation) {
                this.showError({message:'Por favor selecione o local de recolha'})
                return
            }

            this.isSaving = true

            try {
                // Prepare the exact payload your API expects
                const payload = {
                    reservationId: this.reservationId,
                    vehicleId: this.formData.vehicle.id,
                    startDate: new Date(this.formData.startDate),
                    endDate: new Date(this.formData.endDate),
                    pickupLocation: this.formData.pickupLocation
                }

                // Call the update service function (id goes in URL, payload in body)
                const response = await this.store.updateReservation(this.reservationId, payload)

                // Check if the response indicates success
                if (response && response.isSuccess) {
                    // Update original data after successful save
                    this.originalData = JSON.parse(JSON.stringify(this.formData))

                    this.showSuccess({message:'Reserva atualizada com sucesso'})
                    this.isEditMode = false

                    // Clear vehicle store
                    this.vehicleStore.clearAvailableVehicles()
                    this.vehicleStore.clearError()

                    // Reload the reservation to get fresh data from the server
                    await this.loadReservation()
                } else {
                    // Handle API error response
                    throw new Error(response?.error.message || 'Erro desconhecido')
                }

            } catch (error: any) {
                console.error('Error saving reservation:', error)

                // Handle specific error cases based on status code
                if (error.response?.status === 400) {
                    this.showError({message:'Dados inválidos. Verifique os campos preenchidos.', title:'Erro de Validação'})
                } else if (error.response?.status === 404) {
                    this.showError({message:'Reserva não encontrada.', title:'Erro'})
                } else if (error.response?.status === 409) {
                    this.showError({message:'Conflito: O veículo pode já não estar disponível para essas datas.', title:'Conflito'})
                } else {
                    // Try to get error from response structure
                    const errorMessage = error.response?.data?.error ||
                        error.response?.data?.message ||
                        'Erro ao guardar alterações. Tente novamente.'
                    this.showError({message:errorMessage, title:'Erro ao Guardar'})
                }
            } finally {
                this.isSaving = false
            }
        },

        /**
         * Navigate back to reservations list
         */
        goBack() {
            if (this.isEditMode && this.hasChanges) {
                if (confirm('Tem alterações não guardadas. Deseja descartar as alterações?')) {
                    this.navigateBack()
                }
            } else {
                this.navigateBack()
            }
        },

        /**
         * Actually navigate back
         */
        navigateBack() {
            if (this.$router) {
                this.$router.push('/reservations')
            } else {
                window.history.back()
            }
        },

        /**
         * Format date for display
         * @param {number|string|Date} dateValue - Date value to format
         * @returns {string} Formatted date string
         */
        formatDate(dateValue: number | string | Date) {
            if (!dateValue || dateValue === 0) {
                return '-'
            }

            let date
            if (typeof dateValue === 'number') {
                date = new Date(dateValue)
            } else {
                date = new Date(dateValue)
            }

            const formatter = new Intl.DateTimeFormat("pt-PT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false   // garante formato 24h
            })

            return formatter.format(date).replace(",", "")
        },

        /**
         * Get CSS class for status badge
         * @param {string} status - Reservation status
         * @returns {string} CSS class name
         */
        getStatusClass(status: ReservationStatus) {
            const statusClasses = {
                'Open': 'status-open',
                'Confirmed': 'status-confirmed',
                'Canceled': 'status-cancelled',
                'Closed': 'status-closed'
            }
            return statusClasses[status] || 'status-open'
        },
        // Close dropdown if clicked outside
        handleClickOutside(event: { target: any }) {
            const dropdown = this.$refs.dropdown as HTMLElement | null;
            const input = this.$refs.input as HTMLElement;

            // Check if the click happened outside both the dropdown and input field
            if (dropdown && !dropdown.contains(event.target) && !input.contains(event.target)) {
                this.isDropdownOpen = false;
            }
        },

        // Close dropdown if clicked outside
        handleLocationClickOutside(event: { target: any }) {
            const dropdownLocation = this.$refs.dropdownLocation = this.$refs.input as HTMLElement | null;
            const inputLocation = this.$refs.inputLocation as HTMLElement;

            // Check if the click happened outside both the dropdown and input field
            if (dropdownLocation && !dropdownLocation.contains(event.target) && !inputLocation.contains(event.target)) {
                this.isLocationDropdownOpen = false;
            }
        },
    },
    mounted() {
        // Ensure filteredOptions is populated when the component is loaded
        this.filteredOptions = this.vehicleStore.vehicleOptions;

        //Ensure locationFilteredOptions is populated when component is loaded
        this.locationFilteredOptions = this.locationStore.locations;
        // Listen for clicks outside the component to close the dropdown
        document.addEventListener('click', this.handleClickOutside);

        // Listen for clicks outside the component to close the dropdown
        document.addEventListener('click', this.handleLocationClickOutside);
    },
    beforeDestroy() {
        // Remove the event listener when the component is destroyed
        document.removeEventListener('click', this.handleClickOutside);

        // Listen for clicks outside the component to close the dropdown
        document.removeEventListener('click', this.handleLocationClickOutside);
    },
    watch: {
        // Reset filtered options if the available vehicles change
        'vehicleStore.vehicleOptions': function (newOptions) {
            this.filteredOptions = newOptions;
            //console.log('Search term updated:', newOptions)
        },
        // Reset filtered options if the available vehicles change
        'locationStore.locations': function (newOptions) {
            this.locationFilteredOptions = newOptions;
            //console.log('Search term updated:', newOptions)
        }
    },
}
</script>

<style scoped>
/* Form input styles - Made smaller */
.form-input,
.form-textarea,
.form-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background-color: white;
    color: #374151;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
    outline: none;
    border-color: #28b473;
    box-shadow: 0 0 0 2px rgba(40, 180, 115, 0.1);
}

.form-input-readonly,
.form-textarea-readonly {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background-color: #f9fafb;
    color: #374151;
    font-size: 14px;
    cursor: not-allowed;
}

.form-textarea {
    resize: vertical;
    min-height: 60px;
}

.form-textarea-readonly {
    resize: none;
}

.form-datepicker {
    width: 100%;
}

/* Reduced field spacing */
.form-field {
    margin-bottom: 6px;
}

/* Special row groupings */
.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 6px;
}

.form-row-full {
    display: grid;
    grid-template-columns: 1fr;
    margin-bottom: 6px;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 16px;
    font-weight: 500;
}

.status-open {
    background-color: #dbeafe;
    color: #1e40af;
}

.status-confirmed {
    background-color: #dcfce7;
    color: #166534;
}

.status-cancelled {
    background-color: #fee2e2;
    color: #991b1b;
}

.status-closed {
    background-color: #f3e8ff;
    color: #7c2d12;
}

/* Edit restriction message */
.edit-restriction-message {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 4px;
    color: #92400e;
    font-size: 14px;
    margin-bottom: 16px;
}

.restriction-icon {
    flex-shrink: 0;
    color: #d97706;
}

/* Message styles for vehicle selection */
.loading-indicator {
    font-size: 12px;
    color: #6b7280;
    font-weight: normal;
}

.error-text {
    font-size: 12px;
    color: #dc2626;
    margin-top: 4px;
}

.info-text {
    font-size: 12px;
    color: #2563eb;
    margin-top: 4px;
}

.warning-text {
    font-size: 12px;
    color: #d97706;
    margin-top: 4px;
}

/* Container and layout styles */
.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 16px;
}

.page-header h1 {
    font-size: 24px !important;
    margin: 0 !important;
}

.reservation-id {
    font-size: 18px;
    color: #6b7280;
    font-weight: 400;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    text-decoration: none;
    white-space: nowrap;
    min-width: fit-content;
}

.btn:hover:not(:disabled) {
    transform: translateY(-1px);
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-back {
    background-color: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
}

.btn-back:hover {
    background-color: #e5e7eb;
}

.btn-primary {
    background-color: #28b473;
    color: white;
}

.btn-primary:hover {
    background-color: #059669;
}

.btn-secondary {
    background-color: #6b7280;
    color: white;
}

.btn-secondary:hover {
    background-color: #4b5563;
}

.btn-cancel {
    background-color: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
}

.btn-cancel:hover {
    background-color: #e5e7eb;
}

.btn-save {
    background-color: #059669;
    color: white;
}

.btn-save:hover:not(:disabled) {
    background-color: #047857;
}

.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
}

.loading-card {
    background: white;
    border-radius: 6px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    padding: 24px;
    border: 1px solid #e5e7eb;
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.loading-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #28b473;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
}

.loading-text {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
}

.error-container {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 20px;
}

.error-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.error-icon {
    color: #ef4444;
}

.error-title {
    font-size: 16px;
    font-weight: 600;
    color: #991b1b;
    margin: 0;
}

.error-message {
    color: #b91c1c;
    font-size: 14px;
    margin-bottom: 12px;
}

.error-actions {
    display: flex;
    gap: 10px;
}

.form-container {
    background: white;
    border-radius: 6px;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.status-header {
    padding: 12px 20px;
    border-bottom: 1px solid #e5e7eb;
    background-color: #f9fafb;
}

.status-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.status-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.status-label {
    font-size: 16px;
    font-weight: 500;
    color: #6b7280;
}

.status-created {
    font-size: 14px;
    color: #6b7280;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.form-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 3px;
}

.additional-info {
    margin-top: 24px;
}

.form-footer {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

/* Responsive styles */
@media (min-width: 768px) {
    .form-grid {
        grid-template-columns: 1fr;
    }

    .form-footer {
        flex-direction: row;
    }

    .form-footer .btn {
        width: auto;
    }
}

@media (max-width: 767px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .form-footer {
        flex-direction: column;
    }

    .form-footer .btn {
        width: 100%;
    }

    .form-row {
        grid-template-columns: 1fr;
        gap: 12px;
    }
}
</style>